// Host capabilities beyond the basic bridge — fullscreen, native download, closing
// confirmation, write access, home-screen install, haptics, the settings button.
//
// EVERY one of these is CAPABILITY-PROBED, never gated on a platform name: the
// hosts ship overlapping subsets of the same Telegram-shaped SDK, and asking "does
// this bridge expose X, at a high enough version?" is the only thing that stays
// true as they catch up with each other. (Eitaa documents all of these:
// https://developer.eitaa.com/docs/Develop/JsSDK/ — Telegram and the Soroush fork
// expose the same surface under their own globals.)
//
// ⚠️ THE VERSION GATE IS NOT OPTIONAL. Every SDK here silently no-ops a method
// that its reported `version` predates — BackButton needs >= 1.0, fullscreen and
// addToHomeScreen >= 1.3, downloadFile >= 1.4. A host that reports "0.0" (which is
// what you get in a browser with no host hash) accepts every call and does
// nothing, so an un-probed call looks exactly like a broken implementation.
import { useEffect, useState } from "react";
import { hostKey, webApp } from "./platform.js";

// Versions at which each capability appeared (Eitaa's numbering; the Telegram and
// Soroush forks are ahead of these, so the same gate passes there).
const V = {
  backButton:  "1.0",
  fullscreen:  "1.3",
  homeScreen:  "1.3",
  bottomBar:   "1.3",
  tryBrowser:  "1.3",
  downloadFile: "1.4",
  openInvoice: "1.3",   // Bale's own gate for openInvoice; Telegram's is far older
  askReview:   "1.4",   // Bale only so far (invokeCustomMethod "askReview")
};

// Devices on which a host's NATIVE downloadFile really works, where its docs restrict
// it. Eitaa documents downloadFile as «Android only» (developer.eitaa.com JsSDK,
// re-read 2026-10-27) — yet its SDK checks only the version, so on an iPhone or
// desktop client reporting 1.4+ the request would be posted, ignored, and nothing
// would happen (no error → no fallback). There, go straight to the browser route.
const NATIVE_DOWNLOAD_DEVICES = { eitaa: ["android"] };
function nativeDownloadWorksHere(wa) {
  const only = NATIVE_DOWNLOAD_DEVICES[hostKey()];
  return !only || only.includes(String(wa?.platform || "").toLowerCase());
}

/**
 * Does the host support a method introduced at version `v`?
 *
 * ⚠️ `isVersionAtLeast` is NOT universal — BALE'S SDK DOESN'T HAVE IT (its own
 * gates call a private helper). Asking only for that method reported "no" for
 * every capability on Bale, silently switching off features Bale actually has
 * (addToHomeScreen / checkHomeScreenStatus). So fall back to comparing the
 * `version` string the host itself reports, the same way every one of these SDKs
 * does internally: numeric, part by part, missing parts read as 0.
 *
 * A host that reports nothing stays at "0.0" and fails every gate — which is the
 * right answer, since such a host accepts calls and does nothing with them.
 */
export function versionAtLeast(have, want) {
  const a = String(have || "0").split("."), b = String(want || "0").split(".");
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = parseInt(a[i] || "0", 10) || 0, y = parseInt(b[i] || "0", 10) || 0;
    if (x !== y) return x > y;
  }
  return true;
}

export function hostVersionAtLeast(v) {
  try {
    const wa = webApp();
    if (!wa) return false;
    if (typeof wa.isVersionAtLeast === "function") return !!wa.isVersionAtLeast(v);
    return versionAtLeast(wa.version, v);
  } catch (_) { return false; }
}

// ── Haptics ───────────────────────────────────────────────────────────────────
// Free polish, and completely safe: a host without HapticFeedback just no-ops.
export function haptic(kind = "light") {
  try {
    const h = webApp()?.HapticFeedback;
    if (!h) return;
    if (kind === "selection") h.selectionChanged?.();
    else if (kind === "success" || kind === "error" || kind === "warning") h.notificationOccurred?.(kind);
    else h.impactOccurred?.(kind);            // light | medium | heavy | rigid | soft
  } catch (_) {}
}

// ── Fullscreen ────────────────────────────────────────────────────────────────
// The host paints its own close/menu buttons OVER the page in fullscreen, which is
// exactly what safeAreaInset + contentSafeAreaInset describe — MiniApp already
// publishes both as --tg-safe-top/--tg-safe-bottom and re-reads them on
// `fullscreenChanged`, so the layout follows the change with no extra work here.
export function isFullscreen() {
  try { return !!webApp()?.isFullscreen; } catch (_) { return false; }
}

export function requestFullscreen() {
  if (!hostVersionAtLeast(V.fullscreen)) return false;
  try {
    const wa = webApp();
    if (!wa?.requestFullscreen || wa.isFullscreen) return false;
    wa.requestFullscreen();
    return true;
  } catch (_) { return false; }
}

export function exitFullscreen() {
  try { webApp()?.exitFullscreen?.(); } catch (_) {}
}

/**
 * Go fullscreen on mount where the host supports it, and report the live state.
 *
 * `fullscreenFailed` is handled rather than ignored: its error field is UNSUPPORTED
 * or ALREADY_FULLSCREEN, neither of which is recoverable and neither of which
 * matters — the app is already expand()ed, so the non-fullscreen layout is the
 * correct fallback and there is nothing to retry.
 */
export function useFullscreen(enabled = true) {
  const [full, setFull] = useState(isFullscreen);
  useEffect(() => {
    const wa = webApp();
    if (!wa) return;
    const sync = () => setFull(isFullscreen());
    const failed = (e) => {
      setFull(false);
      if (e?.error) console.warn("[host] fullscreen refused:", e.error);
    };
    wa.onEvent?.("fullscreenChanged", sync);
    wa.onEvent?.("fullscreenFailed", failed);
    if (enabled) requestFullscreen();
    sync();
    return () => {
      wa.offEvent?.("fullscreenChanged", sync);
      wa.offEvent?.("fullscreenFailed", failed);
    };
  }, [enabled]);
  return full;
}

// ── Saving a story to the device ──────────────────────────────────────────────
/**
 * Hand a file to the device, by the best route this host actually has.
 *
 *   1. downloadFile({url, file_name})  — NATIVE save, no browser bounce (v1.4+,
 *      Android). This is the real fix for the long-standing "messenger WebViews
 *      block downloads" problem; the two fallbacks below are the old workaround.
 *   2. openLink(url, {try_browser:true}) — force the DEVICE browser (v1.3+), where
 *      Content-Disposition actually triggers a download.
 *   3. openLink(url) / a plain anchor — what we did before.
 *
 * `<a download>` and blob URLs do nothing at all inside these WebViews, so the
 * anchor is a last resort for a desktop browser, never the main path.
 */
export function downloadToDevice(url, fileName) {
  if (!url) return "none";
  const wa = webApp();
  try {
    if (wa?.downloadFile && hostVersionAtLeast(V.downloadFile) && nativeDownloadWorksHere(wa)) {
      wa.downloadFile({ url, file_name: fileName });
      return "native";
    }
  } catch (_) {}
  try {
    if (wa?.openLink && hostVersionAtLeast(V.tryBrowser)) {
      wa.openLink(url, { try_browser: true });
      return "browser";
    }
  } catch (_) {}
  try {
    if (wa?.openLink) { wa.openLink(url); return "link"; }
  } catch (_) {}
  try {
    const a = document.createElement("a");
    a.href = url; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
    return "anchor";
  } catch (_) { return "none"; }
}

// ── Closing confirmation ──────────────────────────────────────────────────────
/**
 * Ask the host to confirm before the app is swiped/closed away, while `active`.
 *
 * Worth it in exactly one place: the ~70-question questionnaire, where a stray
 * back-swipe silently threw away everything the parent had typed. Always paired
 * with a cleanup that disables it again — leaving it on makes every later exit
 * from the app prompt for no reason.
 */
export function useClosingConfirmation(active = true) {
  useEffect(() => {
    const wa = webApp();
    if (!wa || !active) return;
    try { wa.enableClosingConfirmation?.(); } catch (_) {}
    return () => { try { wa.disableClosingConfirmation?.(); } catch (_) {} };
  }, [active]);
}

// ── Write access (so our bot DMs actually land) ───────────────────────────────
/**
 * Ask the host for permission to message this user, once.
 *
 * On Eitaa a DM to a user who never granted write access fails with a 403, and the
 * story-delivery notification IS the product. Opening the Mini App usually grants
 * it implicitly, but "usually" is not a guarantee — this makes it explicit at the
 * one moment it is clearly justified (just before a purchase). Resolves true when
 * already granted or newly allowed, false when declined or unsupported.
 */
export function requestWriteAccess() {
  return new Promise((resolve) => {
    const wa = webApp();
    if (!wa?.requestWriteAccess) { resolve(false); return; }
    let done = false;
    const finish = (ok) => { if (!done) { done = true; resolve(!!ok); } };
    try {
      wa.requestWriteAccess((granted) => finish(granted));
    } catch (_) { finish(false); }
    // Some hosts never invoke the callback when the user dismisses the sheet.
    setTimeout(() => finish(false), 15000);
  });
}

// ── Home screen ───────────────────────────────────────────────────────────────
// A shortcut on the phone's home screen is the retention affordance for a service
// whose whole value arrives twice a week. Android-only (v1.3+); everywhere else
// `status()` resolves "unsupported" and the UI simply never offers it.
export function addToHomeScreen() {
  if (!hostVersionAtLeast(V.homeScreen)) return false;
  try { webApp()?.addToHomeScreen?.(); return true; } catch (_) { return false; }
}

export function homeScreenStatus() {
  return new Promise((resolve) => {
    const wa = webApp();
    if (!wa?.checkHomeScreenStatus || !hostVersionAtLeast(V.homeScreen)) {
      resolve("unsupported"); return;
    }
    let done = false;
    const finish = (s) => { if (!done) { done = true; resolve(s || "unknown"); } };
    try {
      wa.checkHomeScreenStatus((res) => finish(typeof res === "string" ? res : res?.status));
    } catch (_) { finish("unsupported"); }
    setTimeout(() => finish("unsupported"), 6000);
  });
}

/**
 * Whether to offer "add to home screen" right now.
 * Only when the host can do it AND the app is not already there ("missed").
 * `added` / `unknown` / `unsupported` all mean: don't nag.
 */
export function useHomeScreenOffer(enabled = true) {
  const [offer, setOffer] = useState(false);
  useEffect(() => {
    if (!enabled) { setOffer(false); return; }
    let alive = true;
    homeScreenStatus().then((s) => { if (alive) setOffer(s === "missed"); });
    return () => { alive = false; };
  }, [enabled]);
  return offer;
}

// ── Paying without leaving the app ────────────────────────────────────────────
/**
 * Can this host open a payment sheet over the Mini App?
 *
 * Telegram (Stars) and Bale (wallet, v1.3+) both expose `openInvoice`; Eitaa and
 * Soroush don't, and fall back to their hosted gateway. Probed, never named.
 */
export function canOpenInvoice() {
  try { return !!webApp()?.openInvoice && hostVersionAtLeast(V.openInvoice); }
  catch (_) { return false; }
}

/**
 * Open an invoice in place. `link` is whatever the backend handed us, VERBATIM:
 * a t.me invoice URL on Telegram, the bare `invoice_id=…` string on Bale. The
 * callback reports paid | cancelled | failed | pending.
 *
 * ⚠️ The callback is the CLIENT's word, not proof of payment — activation stays
 * server-side, driven by the successful_payment webhook. Use it for the UI only.
 */
export function openInvoiceInApp(link, onClosed) {
  try {
    const wa = webApp();
    if (!link || !wa?.openInvoice) return false;
    wa.openInvoice(link, (res) => {
      const status = typeof res === "string" ? res : (res && res.status);
      try { onClosed && onClosed(status); } catch (_) {}
    });
    return true;
  } catch (_) { return false; }
}

// ── Asking the parent what they thought ───────────────────────────────────────
/**
 * Hand the host its own satisfaction prompt, `delay` seconds from now.
 *
 * Bale ships this as `askReview({delay_seconds})` (v1.4+, over invokeCustomMethod);
 * hosts without it simply no-op. Deliberately asked at ONE moment — after a parent
 * has actually finished listening to a story, i.e. after the product has delivered
 * something — and at most once per person (the caller owns that flag).
 */
export function askReview(delaySeconds = 3) {
  if (!hostVersionAtLeast(V.askReview)) return false;
  try {
    const wa = webApp();
    if (!wa?.askReview) return false;
    wa.askReview({ delay_seconds: delaySeconds });
    return true;
  } catch (_) { return false; }
}

// ── Settings button (the ⋮ in the host's own header) ──────────────────────────
/**
 * Does this host draw a «تنظیمات» item in its own context menu?
 *
 * All four hosts document a `SettingsButton` (Bale v1.0, Eitaa, and the Telegram /
 * Soroush forks). It matters because language + terms now live ONLY behind it —
 * so a host without one has to keep showing them in the page, or they become
 * unreachable. A host reporting "0.0" (a plain browser with no host hash) fails
 * the gate and gets the in-page fallback, which is the right answer.
 *
 * ⚠️ The version numbering is per-host and NOT comparable across them (Telegram
 * gates SettingsButton at 6.10, the Iranian forks renumbered to 1.x), so the only
 * portable gate is "the bridge reports a real version at all".
 */
export function hasSettingsButton() {
  try { return !!webApp()?.SettingsButton && hostVersionAtLeast("1.0"); }
  catch (_) { return false; }
}

export function useSettingsButton(handler, active = true) {
  useEffect(() => {
    const wa = webApp();
    const btn = wa?.SettingsButton;
    if (!btn || !active || typeof handler !== "function") return;
    try {
      btn.onClick?.(handler);
      btn.show?.();
    } catch (_) {}
    return () => {
      try { btn.offClick?.(handler); btn.hide?.(); } catch (_) {}
    };
  }, [handler, active]);
}
